// THE IMPORT CONTROL EDITOR (step 5) — the one step where invoice and line
// values are edited, flags are resolved and the invoice is released (D-215 to
// D-219). The Company is picked here and the save writes it onto the invoice
// (owner item 5, 2026-08-17; code review B-05); the Department is required
// here since D-309, optional from D-217 until then. Every flag resolves by its own Mark
// Resolved, an `auto` one only once its condition holds (D-149). "Release"
// stores whatever has not landed and releases in one request (D-31); the
// server runs the release gates (`assertReleaseGates`) and `canSaveAndRelease`
// below is the client's copy.
//
// An invoice at any other status renders with every control locked
// (`frozen`). A hold stops Release and Archive and nothing else (D-282).
//
// DENIAL REVIEW USES THIS EDITOR TOO (owner, 2026-10-08 — D-328: *"The user
// reviewing an invoice at Denial Review should be able to change any and all of
// the fields, just like a user at Import Control can."*). `step` says which.
// Every field and line is edited the same way and saved by the same request;
// what differs is the action bar — [Hold] [Archive] | [Reinstate], with no
// Release (D-327) — and the Flags section, which is Import Control's alone
// (flags resolve there; past it no step renders one). Reinstate is held to
// Release's own checks, every one but the flags (`canReinstate`), and the
// Company cannot be emptied there: the server refuses a blank one (D-328).
//
// EVERY EDIT SAVES ITSELF (owner item 4, 2026-08-19; D-31 superseded): a pick,
// a removal, a Combine or a changed field's blur saves at once, and typing arms
// one 700ms timer for the whole form, re-armed on each keystroke. There is no
// Save button and no status text in the corner. ONE REQUEST carries the
// header, the changed lines, the removals and, for Release, the release
// itself, as one transaction (`intakeSave`, D-164).
//
// THE STATE IS ONE VALUE (code review D-13, D-284): header,
// rows, Additional Detail and the save counters, changed only by
// `editorReducer` (`../lib/invoiceEditorState.ts`). `latest` always holds the
// newest value, so a save started by a timer, a blur or a pick sends what is on
// screen now. Every write goes through one queue (`createSaveQueue`), so no two
// of this form's requests are ever in flight together. The line table is the
// shared `InvoiceLineItemsTable`, given this form's rows and writes.
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import {
  Body1,
  Button,
  Caption1,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Subtitle2,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
  typographyStyles,
} from '@fluentui/react-components'
import { AppDialog } from './AppDialog'
import { useDialogFormStyles } from './dialogForm'
import { DismissRegular } from '@fluentui/react-icons'
import type { ActiveHold, AssociatedEntity, Flag, Invoice, InvoiceLineItem, RoutingPreview, Upload } from '../lib/types'
import type { RouteOverride, RouteOverrideWrite } from '../api/types'
import {
  chargesHaveALine,
  checkAmounts,
  derivedSubtotal,
  filterMoneyInput,
  formatCurrency,
  formatNumericDate,
  groupedMoney,
  money2,
  showsHeaderCharge,
  sumKnownLineTotals,
  toNumber,
  vendorOptionSubtext,
} from '../lib/format'
import {
  headerGapReasons,
  inactiveAccountReasons,
  isBlank,
  isUnmatchedTag,
  lineGapReasons,
  lineRequiredGaps,
  linesOnInactiveAccount,
} from '../lib/required'
import { AMOUNTS_DO_NOT_RECONCILE, fieldBorder, summaryFieldFlags, summaryFieldTooltip, type SummaryField } from '../lib/flagged'
import { FlagTooltip } from './FlagTooltip'
import {
  DecisionStep,
  DecisionOutcome,
  InvoiceStatus,
  compareFlagsForDisplay,
  flagDetailSequence,
  LINE_SCOPED_FLAG_TYPES,
  flagLabel,
  flagResolution,
  flagDescription,
  ARCHIVE_WARNING,
  ARCHIVE_CATEGORIES,
} from '../lib/constants'
import type { ReasonCategory } from '../lib/constants'
import { DECISION_SOURCE, additionalDetailSections, groupCopy, groupDisagrees, groupTagsDisagree, isLockedSource, realLines, parsePccHistoryDetail } from '@ap/shared'
import {
  applyLineItemMarketingDetailEdit,
  createDecision,
  getInvoice,
  getUpload,
  loadAssociatedEntities,
  intakeSave,
  loadBulkDepartmentOptions,
  loadLineItems,
  resolveFlag,
  searchAccounts,
  searchCompanies,
  searchVendors,
  routingPreview,
  vendorIsInactive,
} from '../lib/data'
import { errorMessage, isApiError } from '../api/errors'
import { useHalfLabels } from '../lib/hooks'
import { EntityPicker, type EntityOption } from './EntityPicker'
import { InvoiceLineItemsTable, type LineItemEditor } from './InvoiceLineItemsTable'
import { ActionDivider } from './ActionDivider'
import { DecisionDialog } from './DecisionDialog'
import { withinDialogPositioning } from './popupPositioning'
import { actionA11y, heldEditorNotice } from '../lib/holds'
import { REINSTATE_BODY } from '../lib/decisions'
import { GLSummary } from './GLSummary'
import { CollapsibleSection } from './CollapsibleSection'
import { unresolvedPill } from '../lib/history'
import { PdfPane } from './PdfPane'
import { FlagChip, DotPill, StatusBadge } from './Badges'
import { TINT_PILLS, useMoneyInputStyles, useRequiredStyles, useSummaryGridStyles } from '../lib/theme'
import { OVERRIDE_LINK, effectiveRoute, routeNotice } from '../lib/routeOverride'
import { RouteOverrideDialog } from './RouteOverrideDialog'
import { buildIntakeSaveBody, chargeMemberIds, groupOperations, type HeaderState, type LineItemState } from '../lib/intakeSaveBody'
import { chargeSpread } from '../lib/taxDistribution'
import {
  editorReducer,
  initialEditorState,
  type EditorAction,
  type LineDetailState,
  type SaveSnapshot,
  editorGroupedRows,
} from '../lib/invoiceEditorState'
import { changedLineIds, conflictRowIsThisInvoice, createSaveQueue, isEnterTarget, isFieldCommitKey, isSelfConflict } from '../lib/saveOnBlur'

// The two duplicate-detection flags (docs/flags.md). Their DETAIL IS AN
// INTERNAL INVOICE ID — the suspected prior invoice — never a value fit to show,
// so it is resolved into the comparison below and never rendered bare.
function isDuplicateFlagType(flagType: string | null | undefined): flagType is 'DuplicateDeterministic' | 'DuplicateFuzzy' {
  return flagType === 'DuplicateDeterministic' || flagType === 'DuplicateFuzzy'
}

// The shape `routes/invoices.ts`'s `requireUuid` checks: a `detail` that fails
// it is never fetched and never shown.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** One duplicate-flag `detail` resolved (or not) to the invoice it names. */
type DuplicateRefState =
  | { status: 'loading' }
  | { status: 'ok'; invoice: Invoice }
  | { status: 'not_found' } // invisible to this user (RLS) or gone — the API cannot tell, and neither does this panel
  | { status: 'error' }

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '16px' },
  // The action bar, pinned to the top of the scrolling body. NO STATUS TEXT in
  // it (owner item 4, 2026-08-19): every child is a button but one, the route
  // line (D-348, D-350), which the owner asked for there. It WRAPS: three
  // buttons of Fluent's 96px floor plus the close button cannot shrink, and
  // without wrapping the close button went off the right edge.
  actionBar: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    justifyContent: 'flex-end',
    padding: '12px 0',
    backgroundColor: tokens.colorNeutralBackground1,
    // Blue rule under the modal's header band (mirrors the page's blue line).
    borderBottom: `3px solid ${tokens.colorBrandStroke1}`,
  },
  // "This invoice will require …" at the bar's left (D-350; until then
  // "Multiple routing rules apply.", D-348), the buttons kept at the right.
  // Plain inline text (owner, 2026-10-12 — D-350 amended: the blue rule and
  // the gray box of the old AI Notes look are gone), in regular weight (amended
  // again the same day: "Can I see ... without the bolding?").
  routingNotice: { marginRight: 'auto' },
  // "Click here to override.", a sentence of its own after the route, in the
  // look of History's "Click here for details." (InvoiceHistory.tsx).
  routingLink: {
    minWidth: 0,
    height: 'auto',
    padding: 0,
    verticalAlign: 'baseline',
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorBrandForegroundLink,
    textDecorationLine: 'underline',
    ':hover': { color: tokens.colorBrandForegroundLinkHover, textDecorationLine: 'underline' },
  },
  // PDF + invoice header side by side; line items full width below.
  topRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '20px',
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
  },
  // THE PDF PANE FOLLOWS THE SUMMARY'S HEIGHT (owner item 3, 2026-08-17): the
  // pane fills an absolutely positioned box, so it adds no height of its own and
  // the field grid sizes the row. `minHeight` is a floor, not a target.
  pdfWrap: {
    position: 'relative',
    // A floor for the one-column layout only; beside the fields the row
    // takes their height. Lowered from 340px when the Tax and Shipping fields
    // left the header (owner, 2026-10-28: the pane ran past the section's rule).
    minHeight: '280px',
    minWidth: 0,
    '@media (max-width: 900px)': { height: '300px' },
  },
  pdfFill: { position: 'absolute', inset: 0 },
  headerCol: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
  flagList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  // Top-aligned, so wrapped help text does not drift the buttons down the row
  // (owner item 10, 2026-08-28).
  flagRow: { display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' },
  // A FIXED PILL COLUMN (owner item 8, 2026-08-31): every caption starts at the
  // same x whatever the label says, and resolving a flag cannot move it.
  flagPillCell: {
    display: 'flex',
    alignItems: 'flex-start',
    flexGrow: 0,
    flexShrink: 0,
    minWidth: '220px',
  },
  // `paddingTop` matches `DotPill`'s, so the caption's first line shares the pill's baseline.
  flagDetail: { color: tokens.colorNeutralForeground2, flex: 1, minWidth: '200px', paddingTop: '3px' },
  // THE ACTION COLUMN, the same width either side of the flip (owner item 8,
  // 2026-08-31), so a resolved row's caption does not reflow.
  flagActions: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: '8px',
    flexWrap: 'wrap',
    flexGrow: 0,
    flexShrink: 0,
    marginLeft: 'auto',
  },
  /** One button — "Mark Resolved" / "Resolved", or a duplicate row's "Compare Invoices". */
  flagActionsOne: { minWidth: '116px' },
  /** Two — a duplicate row's "Archive Invoice" beside "Compare Invoices". */
  flagActionsTwo: { minWidth: '248px' },
  // THE COMPARISON IS THE SAME BOX THE INVOICE MODAL IS (owner, 2026-09-14),
  // reading the `--ip-overlay-*` variables `lib/overlay.ts` measures, so it
  // lands on the modal rather than floating in it.
  compareSurface: {
    position: 'fixed',
    top: 'calc(var(--ip-overlay-top, 72px) + 8px)',
    bottom: '8px',
    left: 'var(--ip-overlay-left, 3vw)',
    width: 'var(--ip-overlay-width, 94vw)',
    maxWidth: 'none',
    height: 'auto',
    maxHeight: 'none',
    margin: '0',
  },
  compareBody: { height: '100%', maxHeight: 'none', display: 'flex', flexDirection: 'column', minHeight: 0 },
  compareContent: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
  // BOTH DOCUMENTS ON SCREEN AT ONCE (owner, 2026-09-14); each pane scrolls its own pages.
  comparePdf: { flex: 1, minHeight: '240px' },
  // The actions at the top, above a blue rule (owner, 2026-09-14).
  compareBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 0 12px',
    borderBottom: `3px solid ${tokens.colorBrandStroke1}`,
    marginBottom: '12px',
  },
  compareBarTitle: { flex: 1, minWidth: 0, ...typographyStyles.subtitle2 },
  // `minWidth: 0` on the columns lets a long vendor name wrap rather than
  // widen the grid past the surface.
  compareGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    flex: 1,
    minHeight: 0,
    '@media (max-width: 720px)': { gridTemplateColumns: '1fr' },
  },
  compareCol: { display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, minHeight: 0 },
  compareHead: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  compareFields: { display: 'flex', flexDirection: 'column', gap: '2px' },
  compareField: { display: 'flex', gap: '6px', alignItems: 'baseline' },
  compareLabel: { color: tokens.colorNeutralForeground3, flexGrow: 0, flexShrink: 0, minWidth: '104px' },
  // A value that DIFFERS between the two invoices: tinted, not merely coloured.
  compareDiff: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorNeutralForeground1,
    borderRadius: tokens.borderRadiusSmall,
    paddingLeft: '4px',
    paddingRight: '4px',
  },
  // Line items scroll inside their own box, never the dialog (app/README.md).
  compareLines: { maxHeight: '160px', overflowY: 'auto', overflowX: 'auto', flexShrink: 0 },
  compareEmpty: { color: tokens.colorNeutralForeground3 },
  // What is stored right now, shown after a concurrent edit collided with this one.
  conflict: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '8px 12px',
    borderLeft: `3px solid ${tokens.colorPaletteRedBorderActive}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
  },
  conflictHead: { fontWeight: tokens.fontWeightSemibold },
})

/**
 * Move focus to the next field someone can type into after `from`, in DOM
 * order (owner item 9, 2026-08-28; QA 2026-10-04, C1-07). Enter uses it, so
 * focus lands somewhere deliberate instead of on `document.body`, where a
 * Fluent Dialog's modalizer hauls it back a beat later (app/README.md). Scoped
 * to the enclosing dialog. Locked boxes, buttons and checkboxes are skipped
 * (`isEnterTarget`): Enter in Shipping used to land on the read-only Subtotal.
 * On the last field the field simply blurs.
 */
function focusNextTabbable(from: HTMLElement): void {
  const scope: ParentNode = from.closest('[role="dialog"]') ?? document
  const candidates = Array.from(scope.querySelectorAll<HTMLElement>('input, select, textarea')).filter(
    (el) =>
      el === from ||
      isEnterTarget({
        tagName: el.tagName,
        type: el instanceof HTMLInputElement ? el.type : undefined,
        readOnly: (el as HTMLInputElement).readOnly === true,
        disabled: el.hasAttribute('disabled'),
        tabIndex: el.tabIndex,
        rendered: el.offsetParent !== null,
        inert: el.closest('[inert]') !== null,
      }),
  )
  const next = candidates[candidates.indexOf(from) + 1]
  if (candidates.indexOf(from) !== -1 && next) next.focus()
  else from.blur()
}

/** The Company field's hover text — a tooltip, never help text under the
    control (owner, 2026-08-10). Worded by the owner, 2026-10-08 (D-334): "We
    should only be using complete sentences with proper grammar." */
export const COMPANY_RULE =
  'The Company is the company being billed, and it determines how the invoice is routed for approval.'

/**
 * What an `auto` flag's condition reads, client-side — the twin of
 * `FlagConditionState` in `backend/src/domain/flagConditions.ts`, built from
 * what is on screen. The resolve handler flushes a pending save first, so the
 * server checks the same values.
 */
interface AutoFlagContext {
  header: HeaderState
  rows: readonly LineItemState[]
  companyId: string | undefined
  /** Active Vehicle/Staff tags with no reference; `null` until read. */
  unmatchedTagCount: number | null
  marketingLinesMissingCategory: number
  /** The lines in the Tax and Shipping groups, which take no Account (D-420). */
  chargeIds: ReadonlySet<string>
}

/** MAY THIS `auto` FLAG BE MARKED RESOLVED YET? (D-149). The server applies
    the same test on the click (409 `flag_condition_unmet`). */
function isAutoSatisfied(flag: Flag, ctx: AutoFlagContext): boolean {
  const { header: h, rows } = ctx
  switch (flag.flag_type) {
    case 'MissingTotal':
      // Entered, and not the 0.00 extraction stores when it read none (code review A-04).
      return h.total.trim() !== '' && toNumber(h.total) !== 0
    case 'MissingExternalInvoiceId':
      return h.externalinvoiceid.trim() !== ''
    case 'MissingInvoiceDate':
      return h.date.trim() !== ''
    case 'MissingVendor':
      return !!h.vendorId
    case 'MissingCompany':
      return !!ctx.companyId
    case 'MissingAccount': {
      // Per line when `detail` names one, else the whole invoice.
      const line = Number.parseInt(String(flag.detail ?? ''), 10)
      // A charge line takes no Account, so it is never missing one (D-420).
      const coded = rows.filter((c) => !ctx.chargeIds.has(c.lineItemId))
      if (Number.isInteger(line)) return coded.filter((c) => c.sequence === line).every((c) => !!c.accountId)
      return coded.length > 0 && coded.every((c) => !!c.accountId)
    }
    case 'LineItemsToTotalMismatch':
      // The rows as they stand, against the Total, within three cents (D-151).
      return checkAmounts(sumKnownLineTotals(rows.map((c) => c.total)), h.tax, h.total, h.shipping).reconciles
    case 'UnmatchedTaggedEntity':
      return ctx.unmatchedTagCount === 0
    case 'MissingMarketingCategory':
      return ctx.marketingLinesMissingCategory === 0
    default:
      return true
  }
}

/** The line a per-line flag is about, or null — shown in front of its standard
    text (owner, 2026-09-28). */
function flagLineNumber(flag: Pick<Flag, 'flag_type' | 'detail'>): number | null {
  return LINE_SCOPED_FLAG_TYPES.has(flag.flag_type ?? '') ? flagDetailSequence(flag.detail) : null
}

/** The tooltip on a disabled Mark Resolved: what is still needed (D-149). */
const AUTO_FLAG_PENDING: Record<string, string> = {
  MissingTotal: 'Enter the Total first. It cannot be 0.00.',
  MissingExternalInvoiceId: 'Enter the External ID first.',
  MissingInvoiceDate: 'Enter the Invoice Date first.',
  MissingVendor: 'Select the Vendor first.',
  MissingCompany: 'The Company must be set first.',
  MissingAccount: 'Select an Account on the line first.',
  LineItemsToTotalMismatch:
    'The amounts must reconcile first: the line items plus Tax plus Shipping must be within $0.03 of the Total.',
  UnmatchedTaggedEntity: 'Correct or remove every unmatched tag first.',
  MissingMarketingCategory: 'Pick a Marketing Category on every Marketing line first.',
}

interface Props {
  invoice: Invoice
  lineItems: InvoiceLineItem[]
  flags: Flag[]
  onChanged: () => void
  /** Called after a release, with where it went (`released_to`, D-237), and
      whether edits typed while the release was out were left unsaved. */
  onReleased: (releasedTo?: string | null, lateEditsUnsaved?: boolean) => void
  /** Called after the record is archived. */
  onArchived?: () => void
  /** The PDF preview, beside the header. */
  pdf?: ReactNode
  /** The modal's History section, first, under the action bar (D-329). */
  history?: ReactNode
  /** Close the modal — the X in the action bar. */
  onClose?: () => void
  /** The invoice's ACTIVE hold, as the screen knows it now. A held invoice
      cannot be released or archived; every field stays editable (D-282). */
  hold?: ActiveHold
  /** The screen's `HoldControl`, rendered immediately left of Archive. */
  holdControl?: ReactNode
  /** Which step is editing: Import Control (the default) or Denial Review
      (D-328). At Denial Review the forward action is Reinstate, not Release,
      and `onReleased` is told where the reinstate sent the invoice. */
  step?: typeof DecisionStep.ImportControl | typeof DecisionStep.DenialReview
}

export function EditableInvoiceForm({
  invoice,
  lineItems,
  flags,
  onChanged,
  onReleased,
  onArchived,
  pdf,
  history,
  onClose,
  hold,
  holdControl,
  step = DecisionStep.ImportControl,
}: Props) {
  const s = useStyles()
  const ms = useMoneyInputStyles()
  const sg = useSummaryGridStyles()
  const df = useDialogFormStyles()
  const rs = useRequiredStyles()
  // The server refuses any save or decision at another status (`out_of_order`),
  // so every control locks on the status alone (D-216).
  const atDenialReview = step === DecisionStep.DenialReview
  const statusFrozen =
    invoice.status !== (atDenialReview ? InvoiceStatus.PendingDenialReview : InvoiceStatus.PendingImportControl)
  const frozen = statusFrozen
  // Release and Archive are blocked by the status AND by a hold (D-282).
  const blocked = statusFrozen || !!hold
  // A LOCKED SOURCE (owner, 2026-10-19 — D-380; the Vendor closed 2026-10-27 —
  // D-421): an invoice copied in from another system keeps agreeing with it, so
  // only each line's Account and Department, and its grouping, may change here. Every other field reads as it does
  // where it cannot be edited, and no line is added, removed or combined. The
  // server refuses the rest (`backend/src/domain/sourceLock.ts`); this only
  // stops offering it.
  const sourceLocked = isLockedSource(invoice.source_system)
  /** The header fields, the Vendor included (D-421): closed by the status or a locked source. */
  const headerLocked = frozen || sourceLocked

  // The Dialog this form is inside: where the row pop-ups mount. A popover
  // portalled to `document.body` sits outside the Dialog's focus trap, and
  // tabster hauled focus back out of it a beat after every click — found with a
  // focus log, not guessed. Mounting inside the Dialog leaves one trap.
  const rootRef = useRef<HTMLDivElement>(null)
  const [dialogEl, setDialogEl] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setDialogEl(rootRef.current?.closest('[role="dialog"]') as HTMLElement | null)
  }, [])

  // ---- The state, and the one ref the saves read ---------------------------
  const [state, setState] = useState(() => initialEditorState(invoice, lineItems))
  const latest = useRef(state)
  const update = useCallback((action: EditorAction) => {
    latest.current = editorReducer(latest.current, action)
    setState(latest.current)
  }, [])
  const { header, rows, details, serverRow } = state
  // The caller's latest `onChanged`, for a save that finishes after a re-render.
  const onChangedRef = useRef(onChanged)
  useEffect(() => {
    onChangedRef.current = onChanged
  })

  const [releasing, setReleasing] = useState(false)
  // Denial Review's forward action (D-328), in place of Release.
  const [reinstateOpen, setReinstateOpen] = useState(false)
  // THE ROUTE (D-350): what the rules or the default route would require for
  // the form as it stands, the Override set on the invoice, and its pop-up.
  const [routing, setRouting] = useState<RoutingPreview | null>(null)
  const [routeOverride, setRouteOverride] = useState<RouteOverride | null>(invoice.route_override ?? null)
  const [overrideOpen, setOverrideOpen] = useState(false)
  const [overrideSaving, setOverrideSaving] = useState(false)
  const [overrideError, setOverrideError] = useState<string | null>(null)
  const [reinstating, setReinstating] = useState(false)
  const [notInvoiceOpen, setNotInvoiceOpen] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [resolving, setResolving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Which header money fields have focus: a focused box shows the raw text, a
  // blurred one the comma-grouped figure (owner item 12, 2026-08-18). State
  // never holds a comma.
  const [editingMoney, setEditingMoney] = useState<ReadonlySet<string>>(() => new Set())
  const moneyFocus = (key: string) => setEditingMoney((prev) => new Set(prev).add(key))
  const moneyBlur = (key: string) =>
    setEditingMoney((prev) => {
      if (!prev.has(key)) return prev
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  const moneyValue = (key: string, raw: string): string => (!headerLocked && editingMoney.has(key) ? raw : groupedMoney(raw))
  // Each focused field's value at focus, keyed `tax`, `description:<id>` and so
  // on: a blur saves only on a real change (D-31), and a save's answer leaves a
  // focused field's text alone (`adoptSave`).
  const fieldSnapshot = useRef<Map<string, string>>(new Map())

  // Duplicate references (Part 1), keyed by the lower-cased detail uuid.
  const [duplicateRefs, setDuplicateRefs] = useState<Record<string, DuplicateRefState>>({})
  const fetchedDuplicateRefs = useRef<Set<string>>(new Set())
  // Per component, not per effect run: the reference fetch is never retried,
  // so a re-run must not drop a result in flight (observed 2026-08-18).
  const mountedRef = useRef(true)
  const [duplicateConfirmFlag, setDuplicateConfirmFlag] = useState<Flag | null>(null)
  const [duplicateArchiving, setDuplicateArchiving] = useState<string | null>(null)
  // The duplicate whose comparison is open (owner item 16, 2026-08-31).
  const [compareFlag, setCompareFlag] = useState<Flag | null>(null)
  const [compareLines, setCompareLines] = useState<
    { status: 'loading' } | { status: 'ok'; lineItems: InvoiceLineItem[] } | { status: 'error' } | null
  >(null)
  // THE INVOICE'S TAGS, read once and re-read when a tag is written: the row
  // icons' blue and red, and `UnmatchedTaggedEntity`'s Mark Resolved. `null`
  // until read, or after a failed read — the button then stays disabled.
  const [tags, setTags] = useState<AssociatedEntity[] | null>(null)

  // ---- Saving --------------------------------------------------------------
  const [queue] = useState(createSaveQueue)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelDebounce = useCallback(() => {
    if (debounceTimer.current !== null) clearTimeout(debounceTimer.current)
    debounceTimer.current = null
  }, [])

  const reloadTags = useCallback(() => {
    loadAssociatedEntities(invoice.id)
      .then((rows) => {
        if (mountedRef.current) setTags(rows)
      })
      .catch(() => {
        // No rings rather than a ring nobody can explain: the flag is the signal.
        if (mountedRef.current) setTags(null)
      })
  }, [invoice.id])

  /** Turn a refusal into something the reviewer can act on. */
  const reportSaveError = useCallback(
    (e: unknown, verb: 'Save' | 'Release') => {
      if (!isApiError(e)) {
        setError(`${verb} failed: ${errorMessage(e)}`)
        return
      }
      if (e.code === 'unresolved_flags') {
        const types = (e.details?.flag_types as string[] | undefined) ?? []
        setError(
          types.length > 0
            ? `Not released — ${types.length === 1 ? 'this flag is' : 'these flags are'} still unresolved: ${types
                // The label the invoice's own flag row carries, from its
                // `flag_definitions` row: a retired flag is in no live copy here.
                .map((t) => flagLabel(t, flags.find((f) => f.flag_type === t)?.label))
                .join(', ')}. Nothing was saved.`
            : `${e.message} Nothing was saved.`,
        )
        return
      }
      if (e.code === 'amounts_do_not_reconcile') {
        setError(`Not released — ${e.message} Nothing was saved.`)
        return
      }
      if (e.code === 'version_conflict') {
        // Show what is stored rather than retry blind over someone's edit — and
        // only when the payload IS this invoice: a line's conflict hands back a
        // LINE (observed 2026-08-19).
        const current = e.details?.current as Invoice | undefined
        if (current && conflictRowIsThisInvoice(current, invoice.id)) {
          update({ type: 'conflict', rowVersion: current.row_version, serverRow: current })
        }
        setError(
          'Someone else changed this invoice while you were editing it, so nothing was saved. Their version is shown below the message; re-apply anything still needed and save again.',
        )
        return
      }
      setError(`${verb} failed: ${e.message}`)
    },
    [flags, invoice.id, update],
  )

  /**
   * ONE SAVE: what is on screen when it starts — the header, the lines whose
   * write signature moved, every pending removal and, when `release` is set,
   * the release. The answer is taken back by `adoptSave`. `raced` says whether
   * the reviewer typed while it was out: what they typed is not stored yet, so
   * a release must not go on (QA Q11).
   */
  const persist = useCallback(
    async (
      release: boolean,
      // The Override pop-up's answer, or null to clear it; absent leaves it (D-350).
      routeOverrideWrite?: RouteOverrideWrite | null,
    ): Promise<{ released: boolean; releasedTo: string | null; raced: boolean; routeOverride: RouteOverride | null }> => {
      const st = latest.current
      const changedIds = changedLineIds(st.rows, st.baseline)
      const sent: SaveSnapshot = {
        header: st.header,
        rows: st.rows,
        removedIds: st.removedIds,
        editSeq: st.editSeq,
        changedIds,
        groups: st.groups,
      }
      const result = await intakeSave(
        invoice.id,
        buildIntakeSaveBody({
          header: st.header,
          rows: st.rows,
          changedIds,
          rowVersion: st.rowVersion,
          lineRowVersion: (id) => st.lineVersions.get(id),
          removed: st.removedIds,
          release,
          routeOverride: routeOverrideWrite,
          // THE GROUP CHANGES (D-399): what is on screen against what is stored.
          groups: { now: st.groups, stored: st.groupBaseline },
        }),
        // The editor's Release is a button in the modal (D-331).
        release ? DECISION_SOURCE.modal : undefined,
      )
      const raced = latest.current.editSeq !== sent.editSeq
      update({ type: 'saved', sent, result, focused: new Set(fieldSnapshot.current.keys()) })
      // A Combine makes a group, and the lines keep their own tags (D-399), so
      // nothing here re-reads them.
      return {
        released: release && !!result.released_to,
        releasedTo: release ? (result.released_to ?? null) : null,
        raced,
        routeOverride: result.invoice.route_override ?? null,
      }
    },
    [invoice.id, update],
  )

  /** An automatic save: reports its own errors, and tells the caller, so the
      queue behind the modal does not keep the pre-save row. */
  const saveJob = useCallback(async () => {
    const sent = latest.current
    if (!sent.dirty) return
    try {
      await persist(false)
      onChangedRef.current()
    } catch (e) {
      if (isApiError(e) && e.code === 'version_conflict') {
        const current = e.details?.current as Invoice | undefined
        // THIS FORM'S OWN EARLIER WRITE, landing late: recover silently (D-31).
        // Never when this save wrote a line of its own — the payload is one
        // row, and `isSelfConflict` compares the header alone (C25, D-164).
        const carriedLineWrites =
          sent.removedIds.length > 0 ||
          changedLineIds(sent.rows, sent.baseline).size > 0 ||
          groupOperations(sent.groups, sent.groupBaseline, sent.rows).length > 0
        if (current && conflictRowIsThisInvoice(current, invoice.id) && !carriedLineWrites && isSelfConflict(sent.header, current)) {
          update({ type: 'conflict', rowVersion: current.row_version, serverRow: null })
          onChangedRef.current()
          return
        }
      }
      reportSaveError(e, 'Save')
    }
  }, [persist, invoice.id, update, reportSaveError])

  /** Save now — a pick, a removal, a Combine, a changed field's blur. The save
      sends the whole form, so the typing timer has nothing left to do. */
  const saveNow = useCallback(() => {
    cancelDebounce()
    queue.request(saveJob)
  }, [cancelDebounce, queue, saveJob])

  /** Typing: one 700ms timer for the whole form, re-armed on every keystroke.
      A half-typed amount ("-", ".") never goes out: blur normalizes it first. */
  const armDebounce = useCallback(() => {
    cancelDebounce()
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null
      const st = latest.current
      if (!st.dirty) return
      const moneyTexts = [st.header.tax, st.header.shipping, st.header.total, ...st.rows.map((r) => r.price)]
      if (moneyTexts.some((v) => v.trim() !== '' && Number.isNaN(Number(v)))) return
      queue.request(saveJob)
    }, 700)
  }, [cancelDebounce, queue, saveJob])

  /** An edit that waits for the typing timer. */
  function edit(action: EditorAction) {
    update(action)
    armDebounce()
  }
  /** An edit that is a whole value already, and saves at once. */
  function commit(action: EditorAction) {
    update(action)
    saveNow()
  }

  const snapshotField = (key: string, value: string) => fieldSnapshot.current.set(key, value)
  /** A blur: save only when the value moved since focus. An unchanged field
      leaves the timer alone — a sibling's edit may be what it is waiting on. */
  function commitIfChanged(key: string, value: string) {
    const before = fieldSnapshot.current.get(key)
    fieldSnapshot.current.delete(key)
    if (before !== undefined && before !== value) saveNow()
  }
  /** ENTER COMMITS THE FIELD (owner item 9, 2026-08-28) by moving focus to the
      next control, so the field's own blur runs — no second save path. */
  function commitOnEnter(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (!isFieldCommitKey(e.key, e)) return
    e.preventDefault()
    focusNextTabbable(e.currentTarget)
  }

  /** One line's Marketing Category, on its own route (§4.10), from what the
      state holds when the write starts. Fleet Maintenance's detail is
      read-only: nothing here writes it (D-381). */
  const saveDetail = useCallback(
    (lineItemId: string) => {
      void queue.run(async () => {
        const d = latest.current.details[lineItemId]
        if (!d) return
        const res = await applyLineItemMarketingDetailEdit(invoice.id, lineItemId, d.marketingCategoryId, d.marketingRowVersion)
        if (res.applied && res.lineItem) {
          update({ type: 'detailSaved', id: lineItemId, lineItem: res.lineItem })
          // The queue's count of lines owing a category, and the flag that
          // reads it, follow the category just written.
          onChangedRef.current()
        } else if (!res.applied) {
          setError(`Could not save Marketing category: ${res.reason}`)
        }
      })
    },
    [queue, invoice.id, update],
  )

  // ONE MARKETING CATEGORY ON SEVERAL LINES — Move to New Group's (D-399) and,
  // since D-415, Update's. Only a saved line has a detail row to write; the
  // bulk row offers no category while an unsaved line is checked.
  const writeCategory = useCallback(
    (ids: ReadonlySet<string>, category: { id: string; label: string; context: string | null }) => {
      const unsaved = new Set(latest.current.rows.filter((r) => r.isNew).map((r) => r.lineItemId))
      for (const id of ids) {
        if (unsaved.has(id)) continue
        update({
          type: 'detail',
          id,
          patch: { marketingCategoryId: category.id, marketingCategory: category.label, marketingCategoryContext: category.context },
        })
        saveDetail(id)
      }
    },
    [update, saveDetail],
  )

  // Leaving the modal: the X saves anything pending (`handleClose`), and so
  // does any other way the form unmounts — Escape, a backdrop click.
  useEffect(
    () => () => {
      cancelDebounce()
      if (latest.current.dirty) queue.request(saveJob)
    },
    [cancelDebounce, queue, saveJob],
  )
  // A TAB CLOSE, RELOAD OR NAVIGATE-AWAY never unmounts React (C17): fire the
  // save and ask the browser to confirm, which buys the request time to land.
  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (!latest.current.dirty) return
      queue.request(saveJob)
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [queue, saveJob])

  function handleClose() {
    if (latest.current.dirty) queue.request(saveJob)
    onClose?.()
  }

  // ---- Reads ---------------------------------------------------------------

  // Resolve every duplicate-flag `detail` into the invoice it names (`getInvoice`),
  // once per id. A malformed detail is never fetched; a 404 — gone, or not this
  // caller's to see — is `not_found`, never the bare id (docs/security.md).
  useEffect(() => {
    const idsToFetch: string[] = []
    for (const f of flags) {
      if (!isDuplicateFlagType(f.flag_type) || !f.detail) continue
      const id = f.detail.toLowerCase()
      if (!UUID_PATTERN.test(id) || fetchedDuplicateRefs.current.has(id)) continue
      fetchedDuplicateRefs.current.add(id)
      idsToFetch.push(id)
    }
    if (idsToFetch.length === 0) return
    setDuplicateRefs((prev) => {
      const next = { ...prev }
      for (const id of idsToFetch) next[id] = { status: 'loading' }
      return next
    })
    for (const id of idsToFetch) {
      getInvoice(id)
        .then((inv) => {
          if (mountedRef.current) setDuplicateRefs((prev) => ({ ...prev, [id]: { status: 'ok', invoice: inv } }))
        })
        .catch((e: unknown) => {
          if (!mountedRef.current) return
          setDuplicateRefs((prev) => ({ ...prev, [id]: { status: isApiError(e) && e.isNotFound ? 'not_found' : 'error' } }))
        })
    }
  }, [flags])

  useEffect(() => {
    reloadTags()
  }, [reloadTags])

  // The other side of the comparison: its header is already in `duplicateRefs`;
  // its lines are read while the dialog is open, once per referenced invoice.
  const compareRefId =
    compareFlag?.detail && UUID_PATTERN.test(compareFlag.detail) ? compareFlag.detail.toLowerCase() : null
  const compareRef: DuplicateRefState | null = compareRefId ? duplicateRefs[compareRefId] ?? null : null
  const compareInvoice = compareRef?.status === 'ok' ? compareRef.invoice : null
  const compareInvoiceId = compareInvoice?.id ?? null
  // AN ORIGINAL IN POINT CLICK CARE (D-387): entered there before this company
  // used the app, so there is no invoice of ours to read. The flag's detail
  // carries the record itself.
  const comparePcc = parsePccHistoryDetail(compareFlag?.detail)
  useEffect(() => {
    if (compareInvoiceId === null) {
      setCompareLines(null)
      return
    }
    let cancelled = false
    setCompareLines({ status: 'loading' })
    loadLineItems(compareInvoiceId)
      .then((lines) => {
        if (!cancelled && mountedRef.current) setCompareLines({ status: 'ok', lineItems: realLines(lines) })
      })
      .catch(() => {
        if (!cancelled && mountedRef.current) setCompareLines({ status: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [compareInvoiceId])

  // Re-armed on mount, not only cleared on unmount: StrictMode mounts twice.
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Warm the pickers' shared empty-term cache, so the first Account picker opens with results.
  useEffect(() => {
    void searchAccounts('')
  }, [])

  const labels = useHalfLabels()
  const storedLines = useMemo(() => new Map(lineItems.map((c) => [c.id, c])), [lineItems])

  // ---- Derived -------------------------------------------------------------

  // THE SUBTOTAL IS THE ROWS' SUM, displayed, never typed (D-3).
  const subtotal = useMemo(() => derivedSubtotal([...rows]), [rows])
  // Lines plus tax plus shipping against the Total, within three cents (D-151).
  const amountsReconcile = checkAmounts(sumKnownLineTotals(rows.map((r) => r.total)), header.tax, header.total, header.shipping).reconciles
  // THE TAX AND SHIPPING GROUPS' LINES (D-420): no Account, Department or
  // Marketing Category, so no gap, red border or GL Summary row of their own.
  const chargeIds = useMemo(() => chargeMemberIds(state.groups), [state.groups])
  const codedRows = rows.filter((r) => !chargeIds.has(r.lineItemId))
  // WHAT THE CSV SPREADS (D-420): the header Tax and Shipping plus the charge
  // lines, over the coded lines — the GL Summary's figures and the gate below.
  const spread = chargeSpread(header.tax, header.shipping, editorGroupedRows(rows, state.groups))
  // A non-zero charge needs a coded line with an amount to be spread over.
  const chargesPlaceable = chargesHaveALine(codedRows.map((r) => r.total), spread.tax, spread.shipping)
  // THE HEADER TAX AND SHIPPING FIELDS (D-420): only on an invoice that stores a
  // non-zero one — from before the change — so it still reconciles.
  const showTax = showsHeaderCharge(invoice.tax)
  const showShipping = showsHeaderCharge(invoice.shipping)

  // The Company is the header's: the save writes it onto the invoice (B-05).
  const companyId = header.companyId
  const companyDisplay = header.companyName || companyId || ''

  // The tags, by line, for the row icons; and how many are unmatched across
  // the invoice, header-level ones included (D-149).
  const entitiesByLineItem = useMemo(() => {
    const grouped: Record<string, AssociatedEntity[]> = {}
    for (const e of tags ?? []) if (e.child_id) (grouped[e.child_id] ??= []).push(e)
    return grouped
  }, [tags])
  const unmatchedTagCount = tags === null ? null : tags.filter(isUnmatchedTag).length

  // Alphabetical by label, ties on the line a per-line flag names (owner item 7, 2026-08-31).
  const sortedFlags = useMemo(() => [...flags].sort(compareFlagsForDisplay), [flags])

  const autoFlagContext: AutoFlagContext = {
    header,
    rows,
    companyId,
    unmatchedTagCount,
    // THE SAME LINES THE SERVER COUNTS, AT THE MOMENT IT COUNTS THEM. The
    // server reads the STORED active lines; this reads the editor's. They can
    // differ only by edits not saved yet — a new line, a removed line, a
    // picked category — and `handleResolve` flushes every one of those (the
    // save and the detail writes share one queue) before it clicks, so the
    // server judges exactly the set this judged.
    marketingLinesMissingCategory: codedRows.filter(
      (r) =>
        additionalDetailSections(labels.detailKind(r.accountId), invoice.source_system).marketing &&
        !details[r.lineItemId]?.marketingCategoryId,
    ).length,
    chargeIds,
  }
  const canMarkResolved = (f: Flag): boolean =>
    !f.resolved_at && (flagResolution(f.flag_type) !== 'auto' || isAutoSatisfied(f, autoFlagContext))

  // EVERY UNRESOLVED FLAG BLOCKS THE RELEASE (D-149): resolved means `resolved_at`.
  const blockingFlags = flags.filter((f) => !f.resolved_at).length
  const unresolved = unresolvedPill(blockingFlags)
  // "Mark All N Resolved" collects exactly the rows whose own button is enabled,
  // never a duplicate — that one is resolved from its comparison (owner item 16).
  const markAllTargets = sortedFlags.filter((f) => !isDuplicateFlagType(f.flag_type) && canMarkResolved(f))

  // EVERY ON-SCREEN FIELD IS REQUIRED (owner, 2026-09-21), the Department too
  // since D-309. Empty shows as a red box and a flat Release, never as text (D-127).
  // The derived fields — Internal ID, Uploaded Date, Subtotal, a line's Total —
  // are always filled, so they are not here.
  const headerMissing = {
    vendor: !header.vendorId,
    company: !companyId,
    externalId: isBlank(header.externalinvoiceid),
    date: isBlank(header.date),
    // Only where the field shows (D-420).
    tax: showTax && isBlank(header.tax),
    shipping: showShipping && isBlank(header.shipping),
    total: isBlank(header.total),
  }
  /** A line's empty required fields and Additional Detail (`lib/required.ts`,
      D-156). Every line needs an Account and, since D-309, a Department. */
  const lineGaps = (r: LineItemState) => {
    const detail: LineDetailState | undefined = details[r.lineItemId]
    return lineRequiredGaps({
      description: r.description,
      price: r.price,
      quantity: r.quantity,
      accountId: r.accountId,
      departmentId: r.departmentId,
      sections: additionalDetailSections(labels.detailKind(r.accountId), invoice.source_system),
      marketingCategoryId: detail?.marketingCategoryId,
    })
  }
  /** Every line with what it lacks, numbered as the # column numbers it (QA C1-01). */
  const lineGapList = rows.map((r, i) => ({
    lineNumber: r.sequence ?? i + 1,
    // A charge line owes only its Description, Price and Quantity (D-420).
    gaps: chargeIds.has(r.lineItemId) ? lineGaps(r).filter((g) => g === 'description' || g === 'price' || g === 'quantity') : lineGaps(r),
  }))
  const anyRequiredMissing = Object.values(headerMissing).some(Boolean) || lineGapList.some((l) => l.gaps.length > 0)
  // A LINE ON AN INACTIVE ACCOUNT must be changed here before Release (owner,
  // 2026-10-07 — D-308): Import Control can still change the Account, and on
  // the Manager route nobody after it can. Its box is red (`InvoiceLineItemsTable`).
  const inactiveAccountWarnings = inactiveAccountReasons(
    linesOnInactiveAccount(
      rows.map((r, i) => ({ lineNumber: r.sequence ?? i + 1, accountId: chargeIds.has(r.lineItemId) ? undefined : r.accountId })),
      (id) => labels.accountInactive(id),
      (id) => labels.account(id),
    ),
  )

  // AN INACTIVE VENDOR must be changed here before Release or Reinstate (code
  // review G-09, 2026-10-17 — D-308's Account rule applied to the Vendor). The
  // picker offers Active vendors only, so only the vendor the invoice already
  // holds can be Inactive; the server is asked about that one. Its box is red.
  const [storedVendorInactive, setStoredVendorInactive] = useState(false)
  useEffect(() => {
    let live = true
    setStoredVendorInactive(false)
    if (invoice.vendor_id) {
      void vendorIsInactive(invoice.vendor_id)
        .then((inactive) => {
          if (live) setStoredVendorInactive(inactive)
        })
        .catch(() => {})
    }
    return () => {
      live = false
    }
  }, [invoice.vendor_id])
  const vendorInactive = !frozen && storedVendorInactive && !!header.vendorId && header.vendorId === invoice.vendor_id

  // A GROUP WHOSE LINES DO NOT AGREE cannot leave as one row (D-399, G-9; since
  // D-406 a field some lines leave empty counts too): the server refuses it as
  // `group_values_disagree`, and the group's own box is blank and red.
  const disagreeingGroups = state.groups.filter((g) => {
    // A charge group's lines hold no values to disagree on (D-420).
    if (g.chargeKind) return false
    const members = rows.filter((r) => g.members.includes(r.lineItemId))
    return groupDisagrees(
      members.map((r) => ({
        account_id: r.accountId ?? null,
        department_id: r.departmentId ?? null,
        marketing_category_id: details[r.lineItemId]?.marketingCategoryId ?? null,
      })),
    )
  })
  // A GROUP WHOSE LINES CARRY DIFFERENT TAGS (owner, 2026-10-28 — D-425:
  // "Associated Entities need to be consistent for all rows in a group"): the
  // server refuses it as `group_entities_disagree`, and the group's icon and
  // arrow are red. A line not saved yet carries none. The Tax and Shipping
  // groups are exempt (D-420).
  const tagDisagreeingGroups = state.groups.filter(
    (g) => !g.chargeKind && groupTagsDisagree(g.members.map((id) => entitiesByLineItem[id] ?? [])),
  )
  // THE GL SUMMARY'S ROWS ARE THE EXPORTED ROWS (D-403): a group whose lines
  // share an Account is one row, at its first line's number, carrying their
  // sum, as the CSV spreads the charges; every other line is its own row. A
  // group whose lines disagree cannot export (G-9), so its lines show as they are.
  const glSummaryRows = (() => {
    const row = (accountId: string | null | undefined, accountLabel: string | null | undefined, total: number, lineNumber: number | null | undefined) => ({
      accountId,
      accountLabel: accountLabel ?? labels.account(accountId ?? null),
      total,
      // The tie-break key: the distributed view spreads across rows as the CSV does (D-151).
      lineNumber,
    })
    const out: ReturnType<typeof row>[] = []
    const shown = new Set<string>()
    for (const g of state.groups) {
      // NOT A GL ROW (D-420): a charge group's lines are spread over the rest.
      if (g.chargeKind) {
        for (const id of g.members) shown.add(id)
        continue
      }
      const members = rows.filter((r) => g.members.includes(r.lineItemId))
      const accountId = groupCopy(members.map((r) => r.accountId ?? null))
      if (members.length === 0 || accountId === null) continue
      for (const r of members) shown.add(r.lineItemId)
      out.push(
        row(
          accountId,
          members[0]?.accountLabel,
          members.reduce((sum, r) => sum + toNumber(r.total), 0),
          Math.min(...members.map((r) => r.sequence ?? Number.MAX_SAFE_INTEGER)),
        ),
      )
    }
    for (const r of rows) if (!shown.has(r.lineItemId)) out.push(row(r.accountId, r.accountLabel, toNumber(r.total), r.sequence))
    return out
  })()
  // A GROUP WITH NO NAME (D-406): a line dragged onto a line standing alone
  // makes one. The server refuses it as `unnamed_group`; its box is red.
  const unnamedGroups = state.groups.filter((g) => !g.chargeKind && g.name.trim() === '')
  const groupWarnings = [
    ...(unnamedGroups.length > 0
      ? [`Give every group a “Description” to ${atDenialReview ? 'reinstate' : 'release'}.`]
      : []),
    ...disagreeingGroups.map((g) =>
      g.name.trim() === ''
        ? `The lines in an unnamed group do not all have the same “Account”, “Department”, and “Marketing Category” — make them the same to ${atDenialReview ? 'reinstate' : 'release'}.`
        : `The lines in the group “${g.name}” do not all have the same “Account”, “Department”, and “Marketing Category” — make them the same to ${atDenialReview ? 'reinstate' : 'release'}.`,
    ),
    ...tagDisagreeingGroups.map((g) =>
      g.name.trim() === ''
        ? `The lines in an unnamed group do not all have the same “Associated Entities” — make them the same to ${atDenialReview ? 'reinstate' : 'release'}.`
        : `The lines in the group “${g.name}” do not all have the same “Associated Entities” — make them the same to ${atDenialReview ? 'reinstate' : 'release'}.`,
    ),
  ]

  // THE SERVER'S RELEASE GATES, client-side (D-218).
  const canSaveAndRelease =
    !blocked &&
    !!companyId &&
    rows.length > 0 &&
    !anyRequiredMissing &&
    inactiveAccountWarnings.length === 0 &&
    disagreeingGroups.length === 0 &&
    tagDisagreeingGroups.length === 0 &&
    unnamedGroups.length === 0 &&
    !vendorInactive &&
    blockingFlags === 0 &&
    amountsReconcile &&
    chargesPlaceable

  // DENIAL REVIEW'S REINSTATE: Release's gates but the flags (owner,
  // 2026-10-08 — D-328), which show at Import Control alone. No tag term:
  // a person cannot save an unmatched tag (D-332). A cleared Company disables
  // it, and its reason names the Company (D-328 amended).
  const canReinstate =
    !blocked &&
    !!companyId &&
    rows.length > 0 &&
    !anyRequiredMissing &&
    inactiveAccountWarnings.length === 0 &&
    disagreeingGroups.length === 0 &&
    tagDisagreeingGroups.length === 0 &&
    unnamedGroups.length === 0 &&
    !vendorInactive &&
    amountsReconcile &&
    chargesPlaceable
  // The verb the reasons below end on: the button they explain.
  const gateVerb = atDenialReview ? 'reinstate' : 'release'

  // THE ROUTE, ON EVERY INVOICE AT THE STEP (owner, 2026-10-12 — D-350: "This
  // invoice will require Controller Approval, Director Approval, and Oversight
  // Approval. Override"; amended the same day to the sentence and "Click here
  // to override.", the sentence no longer bold; until then a notice only when two or more rules
  // applied, D-347, D-348). What the rules or the default route say is asked of
  // the server for the form as it stands — unsaved edits included — a moment
  // after the last edit, so the line follows the fields. THE GROUPS TOO (owner,
  // 2026-10-22: "if a group line is going to influence routing, the user at
  // that screen needs to know that"): the group changes the save would send
  // ride along, so a Combine, a removal from a group or an amount edited inside
  // one routes here as it will on Release. The Override, when one is set, is
  // the route. ADVISORY: the release or reinstate decides the route again from
  // what it saves, and a failed preview shows no line.
  const routeLive = !frozen
  useEffect(() => {
    if (!routeLive) {
      setRouting(null)
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      const st = latest.current
      const body = buildIntakeSaveBody({
        header: st.header,
        rows: st.rows,
        changedIds: changedLineIds(st.rows, st.baseline),
        rowVersion: st.rowVersion,
        lineRowVersion: (id) => st.lineVersions.get(id),
        removed: st.removedIds,
        release: false,
        // What the save would send (`persist`): what is on screen against what is stored.
        groups: { now: st.groups, stored: st.groupBaseline },
      })
      routingPreview(invoice.id, { invoice: body.invoice, line_items: body.line_items, line_groups: body.line_groups })
        .then((preview) => {
          if (!cancelled) setRouting(preview)
        })
        .catch(() => {
          if (!cancelled) setRouting(null)
        })
    }, 400)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [routeLive, header, rows, state.removedIds, state.groups, state.groupBaseline, invoice.id])
  const route = routeLive ? effectiveRoute(routeOverride, routing) : null

  // RED: a required field left empty (D-248), and never on a frozen record.
  // YELLOW: a value tied to an unresolved flag (D-248) — the four amounts go
  // yellow together while the live arithmetic fails (D-148, D-151). A blank
  // Total is the Total's own red, not a reconcile failure.
  const red = (missing: boolean) => !frozen && missing
  // Never red on a field a locked source closes (D-380): nobody can fill it here.
  const redUnlessSourceLocked = (missing: boolean) => !headerLocked && missing
  const amountsDoNotReconcile = rows.length > 0 && !headerMissing.total && !amountsReconcile
  const headerBorder = (isRed: boolean, field: SummaryField) =>
    fieldBorder(isRed, summaryFieldFlags(flags, field, { amountsDoNotReconcile })) === 'flagged' ? rs.flagged : undefined
  // The tooltip names only flags the invoice has (D1-01).
  const headerTip = (isRed: boolean, field: SummaryField) =>
    isRed ? undefined : summaryFieldTooltip(flags, field, { amountsDoNotReconcile })

  // WHY RELEASE IS BLOCKED — ITS TOOLTIP, AND NOWHERE ELSE (D-152): every row
  // in the Flags section is a flag record. Ordered by the control that fixes
  // it, top of the form down. A frozen record contributes none.
  const formWarnings: string[] = []
  if (!frozen) {
    if (!companyId) formWarnings.push(atDenialReview ? 'Set the Company to reinstate.' : 'Set the Company to release.')
    if (rows.length === 0) {
      formWarnings.push(atDenialReview ? 'Add at least one line item to reinstate.' : 'Add at least one line item to release.')
    }
    // Every empty required field by name, from the rule that greys the button (QA C1-01).
    formWarnings.push(
      ...headerGapReasons({
        vendor: headerMissing.vendor,
        externalId: headerMissing.externalId,
        date: headerMissing.date,
        tax: headerMissing.tax,
        shipping: headerMissing.shipping,
        total: headerMissing.total,
      }, gateVerb),
      ...(vendorInactive
        ? [`The Vendor, ${header.vendorName || invoice.vendor_name || header.vendorId}, is Inactive — pick a different “Vendor” to ${gateVerb}.`]
        : []),
      ...lineGapReasons(lineGapList, gateVerb),
      ...inactiveAccountWarnings,
      ...groupWarnings,
    )
    // With no lines the arithmetic cannot reconcile, and the line sentence above names the problem.
    if (rows.length > 0 && !amountsReconcile) formWarnings.push(AMOUNTS_DO_NOT_RECONCILE)
    // The server's `charges_have_a_line` gate: the amounts may add up exactly.
    if (rows.length > 0 && amountsReconcile && !chargesPlaceable) {
      formWarnings.push(`Tax and Shipping are spread over the line items, so at least one line item must carry an amount to ${gateVerb}.`)
    }
  }

  // The Flags section renders on every invoice at Import Control, flags or none
  // (owner, 2026-10-15 — D-366); with none it holds only its heading and the
  // green "0 unresolved" pill, since every row is a flag record (D-152). Flags
  // resolve here, and past it no step renders one — Denial Review included (D-328).
  const flagsSectionShown = !atDenialReview

  // NOT A WARNING: a status this editor does not cover, or a hold, explains the
  // whole surface in its own info bar (owner item 4, 2026-08-28; D-263).
  const frozenNotice = statusFrozen
    ? `This invoice's status is ${invoice.status}, so it cannot be edited ${atDenialReview ? '' : 'or released '}here.`
    : hold
      ? heldEditorNotice(hold, atDenialReview ? 'reinstate' : 'release')
      : undefined

  // Release's tooltip: the flags first — the Flags heading is the next thing
  // the eye lands on (§4.10, closed 2026-08-30) — then every other reason.
  const flagBlockTitle =
    !frozen && blockingFlags > 0
      ? blockingFlags === 1
        ? 'One flag is still unresolved — see Flags below.'
        : `${blockingFlags} flags are still unresolved — see Flags below.`
      : undefined
  const releaseBlockedTitle = frozenNotice ?? ([flagBlockTitle, ...formWarnings].filter(Boolean).join(' ') || undefined)
  // A HOLD IS THE DESCRIPTION, NOT THE NAME (QA C2-04): a held invoice's
  // Release and Archive keep their names. Every other reason stays in the name.
  const heldNotice = !statusFrozen && hold ? frozenNotice : undefined
  const releaseWhy = heldNotice ? { held: heldNotice } : { reason: releaseBlockedTitle }
  // Reinstate's tooltip: Release's reasons minus the flags, else what it does.
  const reinstateBlockedTitle = frozenNotice ?? (formWarnings.join(' ') || undefined)
  const reinstateWhy = heldNotice ? { held: heldNotice } : { reason: reinstateBlockedTitle }
  const archiveWhy = heldNotice ? { held: heldNotice } : { reason: statusFrozen ? frozenNotice : undefined }

  // ---- Actions -------------------------------------------------------------

  // A Mark Resolved is checked against the STORED invoice, so whatever is on
  // screen goes out first, and any save in flight is waited out (D-149).
  async function flushPendingSave(): Promise<void> {
    cancelDebounce()
    if (latest.current.dirty) queue.request(saveJob)
    await queue.run(async () => undefined)
  }

  /** Returns whether the write landed: the comparison closes on success and
      stays open on failure, so its own error bar shows the reason. */
  async function handleResolve(flag: Flag): Promise<boolean> {
    if (resolving) return false
    setResolving(true)
    setError(null)
    try {
      await flushPendingSave()
      await resolveFlag(invoice.id, flag.id)
      onChanged()
      return true
    } catch (e) {
      setError(`Could not mark resolved: ${errorMessage(e)}`)
      return false
    } finally {
      setResolving(false)
    }
  }

  async function handleResolveFromCompare(flag: Flag) {
    if (await handleResolve(flag)) setCompareFlag(null)
  }

  /** "Mark All N Resolved", in turn. A mid-run failure says what already landed
      (`docs/api.md` §5.2 — no partial write); `already_resolved` counts as done. */
  async function handleMarkAllResolved() {
    if (resolving) return
    const targets = markAllTargets
    setResolving(true)
    setError(null)
    let resolvedCount = 0
    let failureReason: string | null = null
    let unreached: Flag[] = []
    try {
      await flushPendingSave()
    } catch (e) {
      failureReason = errorMessage(e)
      unreached = targets
    }
    for (let i = 0; i < targets.length && failureReason === null; i++) {
      try {
        await resolveFlag(invoice.id, targets[i].id)
        resolvedCount++
      } catch (e) {
        if (isApiError(e) && e.code === 'already_resolved') {
          resolvedCount++
          continue
        }
        // Never `detail` in the message — it can carry an id or an invoice number.
        failureReason = errorMessage(e)
        unreached = targets.slice(i)
      }
    }
    onChanged()
    setResolving(false)
    if (failureReason) {
      const names = unreached.map((f) => flagLabel(f.flag_type, f.label))
      const namesText =
        names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')}${names.length > 2 ? ',' : ''} and ${names[names.length - 1]}`
      setError(`Resolved ${resolvedCount} of ${targets.length}. ${namesText} could not be resolved: ${failureReason}.`)
    }
  }

  /**
   * "Archive Invoice" on a duplicate (Part 2, D-165). ORDER IS LOAD-BEARING:
   * resolve the flag FIRST — once the archive lands the invoice is terminal and
   * a resolve would 409, leaving an open flag on a terminal record forever.
   */
  async function handleArchiveDuplicate(flag: Flag, reason: string, category: string) {
    if (duplicateArchiving) return
    setDuplicateArchiving(flag.id)
    setError(null)
    let flagResolved = false
    try {
      await resolveFlag(invoice.id, flag.id)
      flagResolved = true
      onChanged()
      await createDecision({
        invoice_id: invoice.id,
        step,
        decision: DecisionOutcome.Archived,
        reason_category: category as ReasonCategory,
        ...(reason === '' ? {} : { reason }),
        source: DECISION_SOURCE.modal,
      })
      setDuplicateConfirmFlag(null)
      ;(onArchived ?? onReleased)()
    } catch (e) {
      setError(
        flagResolved
          ? `The duplicate flag was marked resolved, but the archive failed: ${errorMessage(e)}. The invoice is still open — resolve any remaining flags and use Save, Release, or Archive from here.`
          : `Could not mark the duplicate flag resolved, so nothing was archived: ${errorMessage(e)}`,
      )
    } finally {
      setDuplicateArchiving(null)
    }
  }

  // Store and release in one request: the server saves, checks the release
  // gates and writes the Import Control approval, or refuses and stores nothing.
  // The Override pop-up's Save & Release comes here too, with its route
  // (D-350 amended): the same save, the same release, the same answers.
  async function handleSaveAndRelease(routeOverrideWrite?: RouteOverrideWrite) {
    setReleasing(true)
    setError(null)
    try {
      const { released, releasedTo, raced, routeOverride: storedOverride } = await queue.run(() =>
        persist(true, routeOverrideWrite),
      )
      // The pop-up closes on any answer: on a release the modal goes with it,
      // and otherwise the reason is the editor's own message, under the bar.
      if (routeOverrideWrite !== undefined) {
        setOverrideOpen(false)
        if (!released) setRouteOverride(storedOverride)
      }
      if (raced && released) {
        // The server stored what was sent and released it; what was typed
        // after the request left was not in it, and the invoice has left
        // Import Control, so it cannot be saved here (code review of D-284).
        cancelDebounce()
        update({ type: 'discard' })
        onReleased(releasedTo, true)
        return
      }
      if (raced) {
        setError('Not released — you changed the invoice while it was saving. Those edits are not stored yet: save again, then release.')
        onChanged()
        return
      }
      if (!released) {
        setError('Saved, but not released. Check the flags and the amounts, then release again.')
        onChanged()
        return
      }
      onReleased(releasedTo)
    } catch (e) {
      // A refused release stores nothing, the Override included.
      if (routeOverrideWrite !== undefined) setOverrideOpen(false)
      reportSaveError(e, 'Release')
    } finally {
      setReleasing(false)
    }
  }

  // Archive a record that is not a payable invoice. The category is required
  // (owner, 2026-09-15); the reason is sent only when typed.
  async function handleNotAnInvoice(reason: string, category: string) {
    setArchiving(true)
    setError(null)
    try {
      // At Denial Review whatever is on screen is stored first, so the
      // correction rows hold every edit made before the archive (D-328).
      if (atDenialReview && !(await storedBeforeDeciding('archived'))) return
      await createDecision({
        invoice_id: invoice.id,
        step,
        decision: DecisionOutcome.Archived,
        reason_category: category as ReasonCategory,
        ...(reason === '' ? {} : { reason }),
        source: DECISION_SOURCE.modal,
      })
      setNotInvoiceOpen(false)
      ;(onArchived ?? onReleased)()
    } catch (e) {
      setError(`Archive failed: ${errorMessage(e)}`)
    } finally {
      setArchiving(false)
    }
  }

  /** Denial Review's Archive and Reinstate store what is on screen first
      (D-328). A save that did not land stops the decision, and says so: the
      edit is neither stored nor audited, and the invoice must not leave with
      the reviewer believing it was. */
  async function storedBeforeDeciding(verb: 'archived' | 'reinstated'): Promise<boolean> {
    await flushPendingSave()
    if (!latest.current.dirty) return true
    setNotInvoiceOpen(false)
    setReinstateOpen(false)
    setError(`Not ${verb} — your latest changes could not be saved. Correct them or reload, then try again.`)
    return false
  }

  // THE OVERRIDE (D-350): stored with whatever else is on screen, in the save
  // queue so it cannot cross a save already out. Nothing moves until the next
  // Release or Reinstate, which applies it.
  async function saveRouteOverride(write: RouteOverrideWrite | null): Promise<boolean> {
    cancelDebounce()
    setOverrideSaving(true)
    setOverrideError(null)
    try {
      const result = await queue.run(() => persist(false, write))
      setRouteOverride(result.routeOverride)
      setOverrideOpen(false)
      onChangedRef.current()
      return true
    } catch (e) {
      setOverrideError(`Could not save the Override: ${errorMessage(e)}`)
      return false
    } finally {
      setOverrideSaving(false)
    }
  }

  // THE POP-UP'S SAVE & RELEASE / SAVE & REINSTATE (owner, 2026-10-12 — D-350
  // amended). At Import Control, the editor's own Release with the Override in
  // the same save. At Denial Review, the Override is saved and then Reinstate's
  // own confirmation asks for its comment, exactly as the Reinstate button does.
  async function saveOverrideAndGo(write: RouteOverrideWrite) {
    if (!atDenialReview) {
      await handleSaveAndRelease(write)
      return
    }
    if (await saveRouteOverride(write)) setReinstateOpen(true)
  }

  // REINSTATE (Denial Review, D-324, D-328): whatever is on screen is stored
  // first, then the invoice goes to its first qualifying approval step. A
  // reason is required.
  async function handleReinstate(reason: string) {
    setReinstating(true)
    setError(null)
    try {
      if (!(await storedBeforeDeciding('reinstated'))) return
      const result = await createDecision({
        invoice_id: invoice.id,
        step,
        decision: DecisionOutcome.Reinstated,
        reason,
        source: DECISION_SOURCE.modal,
      })
      setReinstateOpen(false)
      onReleased(result.new_status)
    } catch (e) {
      setError(`Reinstate failed: ${errorMessage(e)}`)
    } finally {
      setReinstating(false)
    }
  }

  // ---- The line table's row model ------------------------------------------

  const lineEditor: LineItemEditor = {
    rows,
    details,
    storedLines,
    locked: frozen,
    sourceLocked,
    flags,
    popupMountNode: dialogEl,
    setDescription: (id, value) => edit({ type: 'row', id, patch: { description: value } }),
    setAmounts: (id, amounts) => edit({ type: 'amounts', id, ...amounts }),
    focusField: snapshotField,
    blurField: commitIfChanged,
    onFieldKeyDown: commitOnEnter,
    // A new Account drops the Department on screen until the save answers with
    // the one the server derives (D-217); re-picking the same one changes nothing.
    pickAccount: (id, o) => {
      const row = latest.current.rows.find((r) => r.lineItemId === id)
      const changing = !!row && o.id !== row.accountId
      const cascade = changing ? { departmentId: undefined, departmentLabel: undefined, departmentPicked: false } : {}
      commit({ type: 'row', id, patch: { accountId: o.id, accountLabel: o.primary, ...cascade } })
      // THE LONE DEPARTMENT, AT ONCE (owner, 2026-10-23 — D-416): an Account
      // that composes with exactly one Department shows it straight away, as
      // the bulk row does, instead of an empty red box until the save answers.
      // Shown only (not `departmentPicked`): the server still derives it.
      if (!changing) return
      void loadBulkDepartmentOptions([{ account_id: o.id, total: row?.total === undefined || row.total === '' ? null : Number(row.total) }])
        .then((options) => {
          const now = latest.current.rows.find((r) => r.lineItemId === id)
          if (options.length !== 1 || !now || now.accountId !== o.id || now.departmentId) return
          update({ type: 'row', id, patch: { departmentId: options[0]!.department_id, departmentLabel: options[0]!.department_name } })
        })
        .catch(() => undefined)
    },
    // AN EMPTIED ACCOUNT CLEARS THE ACCOUNT AND ITS DEPARTMENT (QA C1-02,
    // D-295); the server clears the Department with it, as on any Account change.
    clearAccount: (id) =>
      commit({
        type: 'row',
        id,
        patch: { accountId: undefined, accountLabel: undefined, departmentId: undefined, departmentLabel: undefined, departmentPicked: false },
      }),
    pickDepartment: (id, o) =>
      commit({ type: 'row', id, patch: { departmentId: o.id, departmentLabel: o.primary, departmentPicked: true } }),
    pickCategory: (id, o: EntityOption) => {
      update({
        type: 'detail',
        id,
        patch: { marketingCategoryId: o.id, marketingCategory: o.primary, marketingCategoryContext: o.secondary ?? null },
      })
      saveDetail(id)
    },
    // A REMOVAL SAVES AT ONCE (D-164): the stored row stays as audit.
    removeRow: (id) => commit({ type: 'removeRow', id }),
    // Delete in the bulk row (D-407): every checked line, in the one save.
    removeRows: (ids) => commit({ type: 'removeRows', ids }),
    addRow: () => edit({ type: 'addRow' }),
    // Apply sets each line's Account and Department as a pick would (D-242).
    applyToRows: (ids, choice) => {
      commit({ type: 'applyToRows', ids, choice })
      // The Marketing Category an Update writes to every line (D-415).
      if (choice.accountId !== undefined && choice.marketing) writeCategory(ids, choice.marketing)
    },
    // THE GROUPS (D-399). Group (Combine until D-407) makes a group of the lines; Ungroup,
    // Move to Existing Group, a drag, a group's own Account or Department, and removing
    // a group row each save at once. A rename saves as typing does.
    groups: state.groups,
    combine: (ids, input) => {
      commit({ type: 'combine', ids, input })
      if (input.marketing) writeCategory(ids, input.marketing)
    },
    removeFromGroup: (ids) => commit({ type: 'removeFromGroup', ids }),
    moveToGroup: (ids, groupKey) => commit({ type: 'moveToGroup', ids, groupKey }),
    renameGroup: (groupKey, name) => edit({ type: 'renameGroup', groupKey, name }),
    ungroup: (groupKey) => commit({ type: 'ungroup', groupKey }),
    // The server raises or clears `UnmatchedTaggedEntity` on every tag write,
    // so the flags re-read too.
    tagsChanged: () => {
      reloadTags()
      onChanged()
    },
  }

  // ---- Duplicate comparison ------------------------------------------------

  // THE STORED RECORDS, not the form state: a duplicate check compares two
  // stored invoices (owner item 16). `differs` is decided on the formatted values.
  // "THIS INVOICE" IS THE EDITOR'S, NOT THE ROW IT OPENED WITH: its header
  // and its lines as they stand on screen, so a Combine, a removed line or an
  // edited value shows in the comparison at once instead of after the modal
  // re-reads (C2 finding, 2026-10-03).
  const comparisonFields = (
    [
      ['Vendor', header.vendorName || invoice.vendor_name, compareInvoice?.vendor_name ?? comparePcc?.vendor_name],
      ['Company', header.companyName || invoice.company_name, compareInvoice?.company_name ?? comparePcc?.company_name],
      ['Invoice Number', header.externalinvoiceid || null, compareInvoice?.external_invoice_id ?? comparePcc?.external_invoice_id],
    ] as [string, string | null | undefined, string | null | undefined][]
  )
    .map(([label, mine, theirs]) => ({ label, mine: mine ?? '—', theirs: theirs ?? '—' }))
    .concat([
      {
        label: 'Invoice Date',
        mine: formatNumericDate(header.date || null),
        theirs: compareInvoice
          ? formatNumericDate(compareInvoice.invoice_date)
          : comparePcc
            ? formatNumericDate(comparePcc.invoice_date)
            : '—',
      },
      {
        label: 'Total',
        mine: formatCurrency(header.total),
        theirs: compareInvoice ? formatCurrency(compareInvoice.total) : comparePcc ? formatCurrency(comparePcc.amount) : '—',
      },
    ])
    .map((f) => ({ ...f, differs: (compareInvoice !== null || comparePcc !== null) && f.mine !== f.theirs }))

  // The four non-happy reference states. Every one still offers Mark Resolved:
  // a duplicate flag's id can name an invoice this reviewer cannot see, or one
  // since removed (docs/ai.md §3.6), and an id that names nothing visible must
  // not trap the invoice.
  const compareUnavailableText = !compareFlag || comparePcc
    ? ''
    : !compareFlag.detail
      ? 'No reference to the original was recorded on this flag, so there is nothing to compare against. Review this invoice and mark the flag resolved if it is not a duplicate.'
      : compareRefId === null
        ? 'The reference to the original could not be read, so there is nothing to compare against. Review this invoice and mark the flag resolved if it is not a duplicate.'
        : compareRef === null || compareRef.status === 'loading'
          ? 'Looking up the original…'
          : compareRef.status === 'not_found'
            ? 'The original cannot be opened from here — it may belong to another company, or it may no longer exist. Review this invoice and mark the flag resolved if it is not a duplicate.'
            : 'The original could not be checked right now. Review this invoice and mark the flag resolved if it is not a duplicate.'

  /** One side's line items, in line order, as plain rows. */
  const compareLineRows = (
    items: readonly { id: string; line_number: number | null | undefined; total: string | number | null | undefined; description: string | null | undefined }[],
  ): ReactNode => {
    if (items.length === 0) return <Caption1 className={s.compareEmpty}>No line items.</Caption1>
    return [...items]
      .sort((a, b) => (a.line_number ?? 0) - (b.line_number ?? 0))
      .map((l) => (
        <div key={l.id} className={s.compareField}>
          <Caption1 className={s.compareLabel}>
            {l.line_number ?? '—'} · {formatCurrency(l.total)}
          </Caption1>
          <Caption1>{l.description || '—'}</Caption1>
        </div>
      ))
  }
  /** The other invoice's stored lines, in the same shape. */
  const storedCompareLines = (items: readonly InvoiceLineItem[]) =>
    items.map((l) => ({ id: l.id, line_number: l.line_number, total: l.total, description: l.description ?? l.description_as_printed }))

  /** A duplicate flag's caption: never the bare `detail`. The Compare Invoices
      button is the way to the original (owner, 2026-09-15). */
  function duplicateFlagCaption(f: Flag): ReactNode {
    const description = flagDescription(f.flag_type, f.description)
    const detail = f.detail
    if (!detail) return description
    if (parsePccHistoryDetail(detail)) return `${description} The original was entered in Point Click Care before this app.`
    if (!UUID_PATTERN.test(detail)) return `${description} The reference to the original could not be read.`
    const ref = duplicateRefs[detail.toLowerCase()]
    if (!ref || ref.status === 'loading') return `${description} Looking up the original…`
    if (ref.status === 'not_found') return `${description} The original cannot be opened from here.`
    if (ref.status === 'error') return `${description} The original could not be checked right now.`
    return description
  }

  const tip = (field: SummaryField, isRed: boolean) => ({
    text: headerTip(isRed, field),
    positioning: withinDialogPositioning(dialogEl, { position: 'above', align: 'center' }),
  })

  /** A header money box: Tax, Shipping or Total. `type="text"` always — a
      blurred box shows "1,234.56", which `type="number"` would blank. */
  const moneyInput = (key: 'tax' | 'shipping' | 'total', isRed: boolean) => (
    <Input
      className={headerBorder(isRed, key)}
      type="text"
      inputMode={headerLocked ? undefined : 'decimal'}
      contentBefore="$"
      input={{ className: ms.moneyText }}
      value={moneyValue(key, header[key])}
      onChange={(_, d) => edit({ type: 'header', patch: { [key]: filterMoneyInput(d.value) } })}
      onFocus={() => {
        moneyFocus(key)
        if (!headerLocked) snapshotField(key, header[key])
      }}
      onBlur={() => {
        moneyBlur(key)
        if (headerLocked) return
        const f = money2(header[key])
        if (f !== header[key]) edit({ type: 'header', patch: { [key]: f } })
        commitIfChanged(key, f)
      }}
      onKeyDown={commitOnEnter}
      readOnly={headerLocked}
      appearance={headerLocked ? 'filled-darker' : undefined}
    />
  )

  // Locked with the rest of the header on a copied invoice (D-421), so never
  // red there: nobody can fill it here.
  const vendorErr = !sourceLocked && red(headerMissing.vendor || vendorInactive)
  const companyErr = redUnlessSourceLocked(headerMissing.company)
  const extIdErr = redUnlessSourceLocked(headerMissing.externalId)
  const dateErr = redUnlessSourceLocked(headerMissing.date)
  const taxErr = redUnlessSourceLocked(headerMissing.tax)
  const shippingErr = redUnlessSourceLocked(headerMissing.shipping)
  const totalErr = redUnlessSourceLocked(headerMissing.total)

  return (
    <div className={s.root} ref={rootRef}>
      {error && (
        <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
      )}

      {/* What is stored, after someone else's edit collided with this one.
          Shown rather than merged: only the reviewer can tell which is right. */}
      {serverRow && (
        <div className={s.conflict}>
          <Caption1 className={s.conflictHead}>Currently Stored</Caption1>
          <Caption1>Company: {serverRow.company_name ?? '—'} · Vendor: {serverRow.vendor_name ?? '—'}</Caption1>
          <Caption1>
            External ID: {serverRow.external_invoice_id ?? '—'} · Invoice Date: {formatNumericDate(serverRow.invoice_date)}
          </Caption1>
          <Caption1>
            {/* Money never renders a dash (owner, 2026-08-19). */}
            Subtotal {formatCurrency(serverRow.subtotal)} · Tax {formatCurrency(serverRow.tax)} · Total {formatCurrency(serverRow.total)}
          </Caption1>
        </div>
      )}

      {frozenNotice && (
        <MessageBar intent="info">
          <MessageBarBody>{frozenNotice}</MessageBarBody>
        </MessageBar>
      )}

      <div className={s.actionBar}>
        {/* THE ONE LINE OF TEXT IN THE BAR (owner, 2026-10-11 — D-348; the
            route on every invoice since D-350), at the left: the route in
            regular weight, then "Click here to override." (D-350 amended). */}
        {route && (
          <Body1 className={s.routingNotice}>
            {routeNotice(route)}{' '}
            <Button
              appearance="transparent"
              className={s.routingLink}
              disabled={blocked}
              onClick={() => {
                setOverrideError(null)
                setOverrideOpen(true)
              }}
            >
              {OVERRIDE_LINK}
            </Button>
          </Body1>
        )}
        {/* HOLD, IMMEDIATELY LEFT OF ARCHIVE (owner, 2026-09-25). "Archive", not
            "Not An Invoice" — that is one of its categories (owner, 2026-09-15). */}
        {holdControl}
        <Button
          appearance="secondary"
          onClick={() => setNotInvoiceOpen(true)}
          disabled={releasing || archiving || blocked}
          title={blocked ? frozenNotice : undefined}
          {...actionA11y('Archive', archiveWhy)}
        >
          Archive
        </Button>
        <ActionDivider />
        {/* DENIAL REVIEW'S FORWARD ACTION IS REINSTATE (D-328): the step has
            no Release. Blocked by the status, by a hold, and by every Release
            check but the flags (`canReinstate`), with the same reasons. */}
        {atDenialReview ? (
          <Button
            appearance="primary"
            onClick={() => setReinstateOpen(true)}
            disabled={!canReinstate || reinstating || archiving}
            title={reinstateBlockedTitle ?? 'Send this invoice to its first qualifying approval step.'}
            {...actionA11y('Reinstate', reinstateWhy)}
          >
            Reinstate
          </Button>
        ) : (
          <Button
            appearance="primary"
            onClick={() => void handleSaveAndRelease()}
            disabled={!canSaveAndRelease || releasing || archiving}
            title={releaseBlockedTitle}
            {...actionA11y(releasing ? 'Releasing…' : 'Release', releaseWhy)}
          >
            {releasing ? 'Releasing…' : 'Release'}
          </Button>
        )}
        {onClose && <Button appearance="subtle" icon={<DismissRegular />} aria-label="Close" onClick={handleClose} />}
      </div>

      {/* ARCHIVE'S CONFIRMATION IS THE SHARED DecisionDialog (owner, 2026-09-25). */}
      <DecisionDialog
        open={notInvoiceOpen}
        title="Are you sure?"
        body={ARCHIVE_WARNING}
        requireReason
        reasonLabel="Comment"
        reasonPlaceholder="This document is a statement. There is nothing to be paid."
        categories={ARCHIVE_CATEGORIES}
        confirmLabel="Archive"
        destructive
        submitting={archiving}
        onCancel={() => setNotInvoiceOpen(false)}
        onConfirm={(reason, category) => void handleNotAnInvoice(reason, category ?? '')}
      />

      {/* THE OVERRIDE POP-UP (owner, 2026-10-12 — D-350). Its Save stores the
          Override with whatever else is on screen, through the editor's own
          save; the next Release or Reinstate applies it. Its Save & Release
          (Save & Reinstate) is the editor's own Release (Reinstate). */}
      <RouteOverrideDialog
        open={overrideOpen}
        override={routeOverride}
        preview={routing}
        hasVendor={!!header.vendorId}
        saving={overrideSaving}
        error={overrideError}
        releaseLabel={atDenialReview ? 'Save & Reinstate' : 'Save & Release'}
        canRelease={(atDenialReview ? canReinstate : canSaveAndRelease) && !releasing && !reinstating && !archiving}
        releaseBlockedTitle={atDenialReview ? reinstateBlockedTitle : releaseBlockedTitle}
        releasing={releasing}
        onCancel={() => setOverrideOpen(false)}
        onSave={(write) => void saveRouteOverride(write)}
        onSaveAndRelease={(write) => void saveOverrideAndGo(write)}
        onRemove={() => void saveRouteOverride(null)}
      />

      {/* REINSTATE'S CONFIRMATION (Denial Review): the screen's own pop-up,
          moved here with the editor (D-328). Reason required, no category. */}
      {atDenialReview && (
        <DecisionDialog
          open={reinstateOpen}
          title="Are you sure?"
          body={REINSTATE_BODY}
          requireReason
          reasonLabel="Comment"
          reasonPlaceholder="The vendor sent the missing receipt, so this can be approved."
          confirmLabel="Reinstate"
          submitting={reinstating}
          onCancel={() => setReinstateOpen(false)}
          onConfirm={(reason) => void handleReinstate(reason)}
        />
      )}

      {/* HISTORY, FIRST (owner, 2026-10-08 — D-329). The modal builds it. */}
      {history}

      {/* "Archive Invoice"'s confirmation is the action bar's Archive pop-up,
          opened on Duplicate (D-165). */}
      <DecisionDialog
        open={!!duplicateConfirmFlag}
        title="Are you sure?"
        body={ARCHIVE_WARNING}
        requireReason
        reasonLabel="Comment"
        reasonPlaceholder="This document is a statement. There is nothing to be paid."
        categories={ARCHIVE_CATEGORIES}
        defaultCategory="duplicate"
        confirmLabel="Archive"
        destructive
        submitting={!!duplicateConfirmFlag && duplicateArchiving === duplicateConfirmFlag.id}
        onConfirm={(reason, category) =>
          duplicateConfirmFlag && void handleArchiveDuplicate(duplicateConfirmFlag, reason, category ?? 'duplicate')
        }
        onCancel={() => setDuplicateConfirmFlag(null)}
      />

      {/* "Compare Invoices" (owner item 16, 2026-08-31): the only surface with a
          duplicate's Mark Resolved, so the comparison is on the path. It opens
          whether or not the original can be shown. */}
      <AppDialog open={!!compareFlag} onOpenChange={(_, d) => { if (!d.open) setCompareFlag(null) }}>
        <DialogSurface className={s.compareSurface}>
          <DialogBody className={s.compareBody}>
            {/* ONE BAR, ONLY THE TITLE IS `DialogTitle` (QA 2026-10-01, D1-02),
                so the dialog is named by its heading and not its buttons. */}
            <div className={s.compareBar}>
              <DialogTitle className={s.compareBarTitle}>Compare Invoices</DialogTitle>
              {/* "Archive Invoice" here too (owner, 2026-09-14): the row's own button. */}
              <Button
                appearance="secondary"
                disabled={releasing || archiving || blocked || duplicateArchiving !== null}
                onClick={() => compareFlag && setDuplicateConfirmFlag(compareFlag)}
              >
                {compareFlag && duplicateArchiving === compareFlag.id ? 'Archiving…' : 'Archive Invoice'}
              </Button>
              <Button
                appearance="primary"
                disabled={resolving}
                onClick={() => compareFlag && void handleResolveFromCompare(compareFlag)}
              >
                {resolving ? 'Marking Resolved…' : 'Mark Resolved'}
              </Button>
              <Button appearance="subtle" icon={<DismissRegular />} aria-label="Close" onClick={() => setCompareFlag(null)} />
            </div>
            <DialogContent className={s.compareContent}>
              {compareFlag && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, minHeight: 0 }}>
                  {/* The form's error bar is behind this dialog, so it shows here too. */}
                  {error && (
                    <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
                  )}
                  {compareInvoice === null && comparePcc === null && (
                    <MessageBar intent="warning">
                      <MessageBarBody>{compareUnavailableText}</MessageBarBody>
                    </MessageBar>
                  )}
                  <div className={s.compareGrid}>
                    <div className={s.compareCol}>
                      <div className={s.compareHead}>
                        <Subtitle2>This Invoice</Subtitle2>
                        <StatusBadge status={invoice.status} />
                      </div>
                      <div className={s.compareFields}>
                        {comparisonFields.map((f) => (
                          <div key={`mine-${f.label}`} className={s.compareField}>
                            <Caption1 className={s.compareLabel}>{f.label}</Caption1>
                            <Caption1 className={f.differs ? s.compareDiff : undefined}>{f.mine}</Caption1>
                          </div>
                        ))}
                      </div>
                      <Caption1 className={s.compareEmpty}>Line Items</Caption1>
                      <div className={s.compareLines}>{compareLineRows(
                        rows.map((r) => ({ id: r.lineItemId, line_number: r.sequence, total: r.total, description: r.description })),
                      )}</div>
                      {/* Both documents, side by side, where the viewer may see
                          documents at all: `pdf` is passed exactly then. */}
                      {pdf && <ComparePdf invoice={invoice} className={s.comparePdf} />}
                    </div>
                    <div className={s.compareCol}>
                      <div className={s.compareHead}>
                        <Subtitle2>The Original</Subtitle2>
                        {compareInvoice && <StatusBadge status={compareInvoice.status} />}
                        {comparePcc && <Caption1>In Point Click Care</Caption1>}
                      </div>
                      {compareInvoice ? (
                        <>
                          <div className={s.compareFields}>
                            {comparisonFields.map((f) => (
                              <div key={`theirs-${f.label}`} className={s.compareField}>
                                <Caption1 className={s.compareLabel}>{f.label}</Caption1>
                                <Caption1 className={f.differs ? s.compareDiff : undefined}>{f.theirs}</Caption1>
                              </div>
                            ))}
                          </div>
                          <Caption1 className={s.compareEmpty}>Line Items</Caption1>
                          <div className={s.compareLines}>
                            {compareLines === null || compareLines.status === 'loading' ? (
                              <Caption1 className={s.compareEmpty}>Loading line items…</Caption1>
                            ) : compareLines.status === 'error' ? (
                              <Caption1 className={s.compareEmpty}>The original’s line items could not be read.</Caption1>
                            ) : (
                              compareLineRows(storedCompareLines(compareLines.lineItems))
                            )}
                          </div>
                          {pdf && <ComparePdf invoice={compareInvoice} className={s.comparePdf} />}
                        </>
                      ) : comparePcc ? (
                        <>
                          <div className={s.compareFields}>
                            {comparisonFields.map((f) => (
                              <div key={`theirs-${f.label}`} className={s.compareField}>
                                <Caption1 className={s.compareLabel}>{f.label}</Caption1>
                                <Caption1 className={f.differs ? s.compareDiff : undefined}>{f.theirs}</Caption1>
                              </div>
                            ))}
                          </div>
                          {/* A warning, not a caption (owner, 2026-10-20): the
                              reviewer has to go to Point Click Care to compare. */}
                          <MessageBar intent="warning">
                            <MessageBarBody>
                              The other invoice was added manually to Point Click Care, without the assist of this application. You must find the invoice there to compare.
                            </MessageBarBody>
                          </MessageBar>
                        </>
                      ) : (
                        <Caption1 className={s.compareEmpty}>{compareUnavailableText}</Caption1>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </AppDialog>

      {/* The heading in the left-hand corner, above the PDF and the fields,
          like every other section's (owner, 2026-10-08 — D-329). */}
      <CollapsibleSection title="Invoice Summary" defaultOpen>
        <div className={s.topRow} style={pdf ? undefined : { display: 'block' }}>
          {pdf && (
            <div className={s.pdfWrap}>
              <div className={s.pdfFill}>{pdf}</div>
            </div>
          )}
          <div className={s.headerCol}>
            {/* THE ONE LINE A LOCKED SOURCE GETS (D-380), above the fields it
                explains. Plain text: no banner, no help under a control. */}
            <div className={mergeClasses(df.grid2, sg.grid)}>
              {/* THE COMPANY IS PICKED HERE (owner item 5, 2026-08-17; D-215).
                  `locked`, not `disabled`, like every locked box (owner item 13,
                  2026-08-18). Its explanation is a tooltip, never a hint. */}
              <Tooltip
                content={[COMPANY_RULE, headerTip(companyErr, 'company')].filter(Boolean).join(' ')}
                relationship="description"
                withArrow
                positioning={withinDialogPositioning(dialogEl, { position: 'above', align: 'center' })}
              >
                <Field label="Company" validationState={companyErr ? 'error' : 'none'}>
                  <EntityPicker
                    ariaLabel="Company"
                    locked={headerLocked}
                    className={headerBorder(companyErr, 'company')}
                    value={{ id: header.companyId, name: companyDisplay }}
                    placeholder="Search companies…"
                    search={(t) =>
                      searchCompanies(t).then<EntityOption[]>((cs) =>
                        cs.map((c) => ({ id: c.company_id ?? '', primary: c.company_name ?? '(unnamed)' })),
                      )
                    }
                    onSelect={(o) => commit({ type: 'header', patch: { companyId: o.id, companyName: o.primary } })}
                    // CLEARING THE FIELD CLEARS IT (D-268), and release then
                    // refuses with "Set the Company to release." (QA C1-02, D-295).
                    // At Denial Review too (owner, 2026-10-08 — D-328 amended:
                    // "Clearing the Company would disable the Reinstate
                    // button."): Reinstate then reads "Set the Company to
                    // reinstate."
                    onClear={() => commit({ type: 'header', patch: { companyId: undefined, companyName: undefined } })}
                  />
                </Field>
              </Tooltip>
              <FlagTooltip {...tip('vendor', vendorErr)}>
                <Field label="Vendor" validationState={vendorErr ? 'error' : 'none'}>
                  <EntityPicker
                    locked={headerLocked}
                    className={headerBorder(vendorErr, 'vendor')}
                    // The wrapping Field cannot name a Combobox nested inside it.
                    ariaLabel="Vendor"
                    value={{ id: header.vendorId, name: header.vendorName }}
                    placeholder="Search vendors…"
                    search={(t) =>
                      searchVendors(t).then<EntityOption[]>((vs) =>
                        vs.map((v) => ({
                          id: v.vendor_id ?? '',
                          primary: v.vendor_name ?? '(unnamed)',
                          // The Vendor ID leads the address (D-208).
                          secondary: vendorOptionSubtext(v),
                        })),
                      )
                    }
                    onSelect={(o) => commit({ type: 'header', patch: { vendorId: o.id, vendorName: o.primary } })}
                    // An emptied Vendor is cleared, and Missing Vendor asks for one (D-295).
                    onClear={() => commit({ type: 'header', patch: { vendorId: undefined, vendorName: undefined } })}
                  />
                </Field>
              </FlagTooltip>
              <FlagTooltip {...tip('internalId', false)}>
                <Field label="Internal ID">
                  <Input className={headerBorder(false, 'internalId')} value={invoice.id ?? '—'} readOnly appearance="filled-darker" />
                </Field>
              </FlagTooltip>
              <FlagTooltip {...tip('externalId', extIdErr)}>
                <Field label="External ID" validationState={extIdErr ? 'error' : 'none'}>
                  <Input
                    className={headerBorder(extIdErr, 'externalId')}
                    value={header.externalinvoiceid}
                    onChange={(_, d) => edit({ type: 'header', patch: { externalinvoiceid: d.value } })}
                    onFocus={() => { if (!headerLocked) snapshotField('externalinvoiceid', header.externalinvoiceid) }}
                    onBlur={() => { if (!headerLocked) commitIfChanged('externalinvoiceid', header.externalinvoiceid) }}
                    onKeyDown={commitOnEnter}
                    readOnly={headerLocked}
                    appearance={headerLocked ? 'filled-darker' : undefined}
                  />
                </Field>
              </FlagTooltip>
              <FlagTooltip {...tip('invoiceDate', dateErr)}>
                <Field label="Invoice Date" validationState={dateErr ? 'error' : 'none'}>
                  {/* `text` while locked: a readOnly `type="date"` still opens the
                      calendar. The value is formatted to match then, and handed
                      back as ISO the moment it is editable. */}
                  <Input
                    className={headerBorder(dateErr, 'invoiceDate')}
                    type={headerLocked ? 'text' : 'date'}
                    value={headerLocked ? formatNumericDate(header.date) : header.date}
                    onChange={(_, d) => edit({ type: 'header', patch: { date: d.value } })}
                    onFocus={() => { if (!headerLocked) snapshotField('date', header.date) }}
                    onBlur={() => { if (!headerLocked) commitIfChanged('date', header.date) }}
                    onKeyDown={commitOnEnter}
                    readOnly={headerLocked}
                    appearance={headerLocked ? 'filled-darker' : undefined}
                  />
                </Field>
              </FlagTooltip>
              <FlagTooltip {...tip('uploadedDate', false)}>
                <Field label="Uploaded Date">
                  <Input className={headerBorder(false, 'uploadedDate')} value={formatNumericDate(invoice.uploaded_at)} readOnly appearance="filled-darker" />
                </Field>
              </FlagTooltip>
              {/* THE TWO CHARGES, THEN THE TWO SUMMARY FIGURES (owner, 2026-08-14).
                  ONLY ON AN INVOICE THAT STORES ONE (D-420): tax and shipping
                  are lines in the Tax and Shipping groups now. */}
              {showTax && (
                <FlagTooltip {...tip('tax', taxErr)}>
                  <Field label="Tax" validationState={taxErr ? 'error' : 'none'}>
                    {moneyInput('tax', taxErr)}
                  </Field>
                </FlagTooltip>
              )}
              {/* SHIPPING (D-1): the totals-block freight charge. An itemized
                  freight LINE stays a line item. */}
              {showShipping && (
                <FlagTooltip {...tip('shipping', shippingErr)}>
                  <Field label="Shipping" validationState={shippingErr ? 'error' : 'none'}>
                    {moneyInput('shipping', shippingErr)}
                  </Field>
                </FlagTooltip>
              )}
              {/* SUBTOTAL IS LOCKED AND DERIVED (D-3). */}
              <Tooltip
                content={['Subtotal is the sum of all line items.', headerTip(false, 'subtotal')].filter(Boolean).join(' ')}
                relationship="description"
                withArrow
                positioning={withinDialogPositioning(dialogEl, { position: 'above', align: 'center' })}
              >
                <Field label="Subtotal">
                  <Input
                    className={headerBorder(false, 'subtotal')}
                    type="text"
                    contentBefore="$"
                    input={{ className: ms.moneyText }}
                    value={groupedMoney(subtotal)}
                    readOnly
                    appearance="filled-darker"
                  />
                </Field>
              </Tooltip>
              <FlagTooltip {...tip('total', totalErr)}>
                <Field label="Total" validationState={totalErr ? 'error' : 'none'}>
                  {moneyInput('total', totalErr)}
                </Field>
              </FlagTooltip>
              {/* No Source field and no lock line (owner, 2026-10-28 — D-423):
                  the locked boxes say enough. D-380 showed both. */}
              {/* NO PRIOR BALANCE FIELD (owner item 3, 2026-08-17: the amount is in
                  AI Notes, `pass1-extraction.md` "Prior balances") and NO
                  CURRENCY FIELD (D-8). */}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Line Items Summary" defaultOpen>
        <InvoiceLineItemsTable
          editor={lineEditor}
          // IMPORT CONTROL'S LINE RULES AT BOTH STEPS (D-328): the table decides
          // what is editable and required by screen, and Denial Review edits
          // exactly what Import Control does.
          screen={DecisionStep.ImportControl}
          invoice={invoice}
          // D-418: the group-name pickers follow the Vendor as it stands here,
          // an unsaved pick included; no line moves when it changes.
          groupVendorId={header.vendorId ?? null}
          subtotal={subtotal}
          tax={header.tax}
          shipping={header.shipping}
          total={header.total}
          entitiesByLineItem={entitiesByLineItem}
        />
      </CollapsibleSection>

      {/* THE FLAGS SECTION: every row a flag record (D-152), every flag on the
          invoice (D-219). Below Line Items Summary, open by default (owner,
          2026-10-08 — D-329 amended: "I think that Flags should be
          automatically expanded, as well."). Import Control only: no other
          modal has one. */}
      {flagsSectionShown && (
        <CollapsibleSection
          title="Flags"
          defaultOpen
          // "3 unresolved", a pill since D-329: yellow above zero, green at
          // zero. It counts what the release gate counts.
          adornments={<DotPill pill={unresolved.pill}>{unresolved.label}</DotPill>}
          actions={
            markAllTargets.length >= 2 && !frozen ? (
              <Button size="small" disabled={resolving} onClick={() => void handleMarkAllResolved()}>
                Mark All {markAllTargets.length} Resolved
              </Button>
            ) : undefined
          }
        >
          <div className={s.flagList}>
            {sortedFlags.map((f) => {
              const resolved = !!f.resolved_at
              const duplicate = isDuplicateFlagType(f.flag_type)
              // Why this row's Mark Resolved is disabled: on its tooltip (D-149).
              const pendingReason =
                !resolved && !canMarkResolved(f)
                  ? (AUTO_FLAG_PENDING[f.flag_type ?? ''] ?? 'This flag cannot be marked resolved yet.')
                  : undefined
              return (
                <div key={f.id} className={s.flagRow}>
                  <span className={s.flagPillCell}>
                    {resolved ? (
                      <DotPill pill={TINT_PILLS.green}>{flagLabel(f.flag_type, f.label)}</DotPill>
                    ) : (
                      <FlagChip flag={f.flag_type} label={f.label} />
                    )}
                  </span>
                  {/* The flag's own description, resolved or not (D-150);
                      `detail` is never appended (owner item 6, 2026-08-28). */}
                  <Caption1 className={s.flagDetail}>
                    {duplicate ? (
                      duplicateFlagCaption(f)
                    ) : flagLineNumber(f) !== null ? (
                      <>
                        <strong>Line {flagLineNumber(f)}:</strong> {flagDescription(f.flag_type, f.description)}
                      </>
                    ) : (
                      flagDescription(f.flag_type, f.description)
                    )}
                  </Caption1>
                  <span className={mergeClasses(s.flagActions, duplicate ? s.flagActionsTwo : s.flagActionsOne)}>
                    {resolved ? (
                      <Tooltip
                        content={`Resolved by ${f.resolved_by_name ?? 'a user'}.`}
                        relationship="description"
                        withArrow
                        positioning={withinDialogPositioning(dialogEl, { position: 'above' })}
                      >
                        <Button size="small" disabledFocusable>
                          Resolved
                        </Button>
                      </Tooltip>
                    ) : duplicate ? (
                      <>
                        <Button
                          size="small"
                          appearance="secondary"
                          disabled={releasing || archiving || blocked || duplicateArchiving !== null}
                          onClick={() => setDuplicateConfirmFlag(f)}
                        >
                          {duplicateArchiving === f.id ? 'Archiving…' : 'Archive Invoice'}
                        </Button>
                        {/* A duplicate's Mark Resolved lives in the comparison (owner item 16). */}
                        <Button size="small" disabled={frozen} onClick={() => setCompareFlag(f)}>
                          Compare Invoices
                        </Button>
                      </>
                    ) : pendingReason ? (
                      // `disabledFocusable`, so the tooltip and a screen reader still reach it.
                      <Tooltip
                        content={pendingReason}
                        relationship="description"
                        withArrow
                        positioning={withinDialogPositioning(dialogEl, { position: 'above' })}
                      >
                        <Button size="small" disabledFocusable>
                          Mark Resolved
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button size="small" disabled={resolving || frozen} onClick={() => void handleResolve(f)}>
                        Mark Resolved
                      </Button>
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        </CollapsibleSection>
      )}

      {/* GL Summary — grouped by ACCOUNT here, because the Department is
          optional at Import Control (D-217). It recomputes from the rows. */}
      <CollapsibleSection title="GL Summary">
        <GLSummary
          groupBy="account"
          subtotal={subtotal}
          // What the CSV spreads over the GL rows: the header charges plus the
          // Tax and Shipping groups' lines (D-420).
          tax={spread.tax}
          shipping={spread.shipping}
          total={header.total}
          rows={glSummaryRows}
        />
      </CollapsibleSection>
    </div>
  )
}

/**
 * One invoice's own pages inside a comparison column (owner, 2026-09-14). It
 * reads its OWN upload: the two columns are two invoices from two packets, and
 * the form holds neither record. No Full Screen button: the comparison already
 * fills the modal's box. A missing upload is `PdfPane`'s own placeholder.
 */
function ComparePdf({ invoice, className }: { invoice: Invoice; className: string }) {
  const [upload, setUpload] = useState<Upload | undefined | 'loading'>('loading')
  const uploadId = invoice.upload_id ?? null
  useEffect(() => {
    if (uploadId === null) {
      setUpload(undefined)
      return
    }
    let cancelled = false
    setUpload('loading')
    getUpload(uploadId)
      .then((u) => {
        if (!cancelled) setUpload(u)
      })
      .catch(() => {
        if (!cancelled) setUpload(undefined)
      })
    return () => {
      cancelled = true
    }
  }, [uploadId])
  return (
    <div className={className}>
      <PdfPane upload={upload} pageStart={invoice.upload_page_start ?? undefined} pageEnd={invoice.upload_page_end ?? undefined} />
    </div>
  )
}
